import { useCallback, useEffect, useRef, useState } from 'react';
import { crypto as cv, type dto, type infra } from '@photo-vault/shared';
import { useInfra } from '../../infra/context.js';
import { getDecryptPool } from '../viewer/decryptPool.js';
import { getCachedRatings, putCachedRatings } from './ratingCache.js';

export interface RatedRow {
  photo: dto.Photo;
  /** 1..5 stars. Server already filters to rated-only, so this is never null. */
  rating: number;
  filename: string;
  mimeType?: string;
  /** Presigned GET URL — null until the chunk lands from /downloads/batch. */
  url: string | null;
}

export interface UseDecryptedRatingsState {
  /** Sorted by rating desc, ties broken by createdAt desc. */
  rows: RatedRow[];
  /** True while a page is currently being fetched + decrypted. */
  loading: boolean;
  /** True once we know the server has more pages we haven't fetched. */
  hasMore: boolean;
  /** Number of (rated) photos drained so far — drives the progress label. */
  totalLoaded: number;
  /** Number of pages we've actually drained from the server (rated or not). */
  pagesDrained: number;
  error: string | null;
  /** Manually drain one more page. No-op while loading or when !hasMore. */
  loadMore: () => void;
  /**
   * Optimistic in-place rating mutation. Pass `null` to drop the row from the
   * gallery (rating cleared → photo no longer matches the server's
   * rated-only filter). Returns a rollback function the caller can invoke if
   * the network round-trip fails or the server reports a keyGen conflict.
   */
  setRowRating: (photoId: string, next: number | null) => () => void;
}

// Initial pages auto-drained on mount. Tuned for the common case: a "casual"
// rater has tens of 5★ in a root, and at PAGE_SIZE=500 server-side scan with
// `attribute_exists(encryptedRating)` filter the first 1-3 pages typically
// surface every rated photo OR enough of them to cover the visible viewport.
// The user can click "Carica altre" to keep going past this budget.
//
// The cap matters even with a server-side filter because the Query still
// pays RCU for the unrated rows it reads — auto-draining the entire root on
// page mount would be wasteful for a passing visit.
const INITIAL_PAGE_BUDGET = 3;

// Drains rated photos under a root, page by page, into a single sorted
// array. The server already filters out unrated photos via
// `attribute_exists(encryptedRating)`, so the client only sees / decrypts
// what it would actually display.
//
// Strategy:
//  - On mount: auto-drain up to INITIAL_PAGE_BUDGET pages so the first
//    paint has something useful and the user doesn't have to click anything
//    just to see their top-rated photos.
//  - After the budget: stop and expose `hasMore` + `loadMore()` so the page
//    can render a "Carica altre" affordance. No infinite background polling.
//  - Per page: rating decrypt hits the IDB cache first; cache misses go
//    through the worker pool. Filename + mimeType decrypt on main thread
//    (small JSON, async). Presigned URLs streamed in via /downloads/batch.
//  - After every page, the rows array is re-sorted (by rating desc,
//    createdAt desc) so high-rated photos surface as soon as they appear.
export function useDecryptedRatings(
  rootId: string | undefined,
  albumKey: CryptoKey | null,
  jwt: string,
): UseDecryptedRatingsState {
  const { metadata } = useInfra();
  const [rows, setRows] = useState<RatedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [pagesDrained, setPagesDrained] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Cursor + cancellation state lives in refs so loadMore() can pick up
  // where the auto-drain left off without re-running the effect.
  const cursorRef = useRef<string | undefined>(undefined);
  const cancelledRef = useRef(false);
  const loadingRef = useRef(false);

  const drainOnePage = useCallback(async () => {
    if (loadingRef.current) return;
    if (!rootId || !albumKey) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const page = await metadata.listRootPhotos(rootId, jwt, cursorRef.current);
      if (cancelledRef.current) return;
      const newRows =
        page.photos.length > 0
          ? await hydratePage(
              metadata,
              page.photos,
              rootId,
              albumKey,
              jwt,
              () => cancelledRef.current,
            )
          : [];
      if (cancelledRef.current) return;
      cursorRef.current = page.nextCursor;
      setHasMore(Boolean(page.nextCursor));
      setPagesDrained((n) => n + 1);
      if (newRows.length > 0) {
        setRows((prev) => {
          const merged = [...prev, ...newRows];
          sortByRating(merged);
          return merged;
        });
      }
    } catch (err) {
      if (!cancelledRef.current) setError(err instanceof Error ? err.message : 'errore');
    } finally {
      loadingRef.current = false;
      if (!cancelledRef.current) setLoading(false);
    }
  }, [rootId, albumKey, jwt, metadata]);

  useEffect(() => {
    if (!rootId || !albumKey) return;
    cancelledRef.current = false;
    cursorRef.current = undefined;
    setRows([]);
    setHasMore(false);
    setPagesDrained(0);
    setError(null);

    void (async () => {
      for (let i = 0; i < INITIAL_PAGE_BUDGET; i++) {
        if (cancelledRef.current) return;
        await drainOnePage();
        if (cancelledRef.current) return;
        // Stop early if the server has no more pages — `cursorRef` mirrors
        // `hasMore`, but reading it directly avoids a stale-closure hop.
        if (!cursorRef.current) break;
      }
    })();

    return () => {
      cancelledRef.current = true;
    };
  }, [rootId, albumKey, jwt, drainOnePage]);

  const loadMore = useCallback(() => {
    if (loadingRef.current) return;
    if (!cursorRef.current) return;
    void drainOnePage();
  }, [drainOnePage]);

  const setRowRating = useCallback((photoId: string, next: number | null): (() => void) => {
    let snapshot: RatedRow[] = [];
    setRows((curr) => {
      snapshot = curr;
      if (next === null) {
        return curr.filter((r) => r.photo.id !== photoId);
      }
      const updated = curr.map((r) => (r.photo.id === photoId ? { ...r, rating: next } : r));
      sortByRating(updated);
      return updated;
    });
    // Rollback restores the captured snapshot wholesale. Safe because rows
    // is the only state the optimistic write mutated; other state (cursor,
    // pagesDrained, hasMore) is unaffected by a rating change.
    return () => setRows(snapshot);
  }, []);

  return {
    rows,
    loading,
    hasMore,
    totalLoaded: rows.length,
    pagesDrained,
    error,
    loadMore,
    setRowRating,
  };
}

function sortByRating(rows: RatedRow[]): void {
  rows.sort((a, b) => {
    if (a.rating !== b.rating) return b.rating - a.rating;
    // Tiebreak by createdAt desc (newer first) — matches Apple Photos style
    // for "favorites".
    return b.photo.createdAt.localeCompare(a.photo.createdAt);
  });
}

async function hydratePage(
  metadata: infra.MetadataStore,
  photos: dto.Photo[],
  rootId: string,
  albumKey: CryptoKey,
  jwt: string,
  isCancelled: () => boolean,
): Promise<RatedRow[]> {
  if (photos.length === 0) return [];

  // Server contract: every photo here has encryptedRating set. We still defend
  // against a stale row missing the attribute (filter races, future schema
  // changes) by simply skipping it — never crash the page.
  const ratingTargets = photos.filter((p): p is dto.Photo & { encryptedRating: string } =>
    Boolean(p.encryptedRating),
  );
  const cached = await getCachedRatings(
    rootId,
    ratingTargets.map((p) => ({ photoId: p.id, encryptedRating: p.encryptedRating })),
  );
  if (isCancelled()) return [];

  const ratingByPhotoId = new Map<string, number>();
  for (const p of ratingTargets) {
    const hit = cached.get(p.id);
    if (hit !== undefined) ratingByPhotoId.set(p.id, hit);
  }

  const toDecrypt = ratingTargets.filter((p) => !cached.has(p.id));
  if (toDecrypt.length > 0) {
    const decoded = await getDecryptPool().decryptTextsBatch(
      toDecrypt.map((p) => p.encryptedRating),
      albumKey,
    );
    if (isCancelled()) return [];
    const toCache: { photoId: string; encryptedRating: string; rating: number }[] = [];
    decoded.forEach((text, i) => {
      const p = toDecrypt[i]!;
      if (text === null) return;
      const n = Number(text);
      if (!Number.isInteger(n) || n < 1 || n > 5) return;
      ratingByPhotoId.set(p.id, n);
      toCache.push({ photoId: p.id, encryptedRating: p.encryptedRating, rating: n });
    });
    void putCachedRatings(rootId, toCache);
  }

  // Filename + mimeType for the tile labels. Small JSON payloads — decrypting
  // on main thread mirrors AlbumPhotosSection / TagDetailPage and stays fast.
  const meta = await Promise.all(
    photos.map(async (p) => {
      try {
        const json = await cv.decryptText(p.encryptedMeta, albumKey);
        const parsed = JSON.parse(json) as { filename?: string; mimeType?: string };
        return { filename: parsed.filename ?? p.id, mimeType: parsed.mimeType };
      } catch {
        return { filename: '(meta non leggibile)', mimeType: undefined as string | undefined };
      }
    }),
  );
  if (isCancelled()) return [];

  // Build only rows whose rating decrypted successfully — a row without a
  // rating has nothing to contribute to a top-rated gallery.
  const rows: RatedRow[] = [];
  photos.forEach((p, i) => {
    const rating = ratingByPhotoId.get(p.id);
    if (rating === undefined) return;
    const m = meta[i]!;
    const row: RatedRow = {
      photo: p,
      rating,
      filename: m.filename,
      url: null,
    };
    if (m.mimeType) row.mimeType = m.mimeType;
    rows.push(row);
  });

  if (rows.length === 0) return rows;

  // Presigned GETs only for the photos we'll actually render. Collect into a
  // local map and patch rows in-place; the parent setState fires once after
  // this function resolves so tiles for the page light up together.
  const urlByPhotoId = new Map<string, string>();
  await metadata.downloadsBatchAllStreaming(
    rows.map((r) => ({ albumId: r.photo.albumId, photoId: r.photo.id })),
    jwt,
    (chunk) => {
      if (isCancelled()) return;
      for (const it of chunk) urlByPhotoId.set(it.photoId, it.url);
    },
  );
  for (const r of rows) {
    const u = urlByPhotoId.get(r.photo.id);
    if (u) r.url = u;
  }
  return rows;
}
